/**
 * Borrowing entity.
 *
 * Entity data is LOAN-LEVEL, not per-borrower. The form used to collect
 * `entityName` + `borrowerEntityType` inside each applicant card, which was
 * wrong twice over: `borrowerEntityType` exists nowhere in the backend (verified
 * — zero references), and entity data lives on the loan's flat `entities[]`
 * array, resolved through `borrowerEntity: true` / `parentEntityId` rather than
 * hanging off a borrower.
 *
 * `UnderwritingBaseModel.entities` is an untyped `list`, and `create_loan` runs
 * it through `flatten_entities`, so posting inline entity objects is the
 * supported shape — no separate create-entity call needed.
 *
 * ONE entity, deliberately: a sizer scenario has a single borrowing entity, and
 * the multi-entity / parent-ownership structures the model supports belong to a
 * real loan, not a pricing run. `SizerScreen` stamps `borrowerEntity: true` when
 * it builds the payload (by convention the borrower entity is also `entities[0]`).
 */
import { useField } from "react-final-form";

import {
  Section,
  Grid,
  Field2,
  RffStepper,
  RffText,
  RffSelect,
} from "../../sizersShared/sizerUi";
import { selectOptionsEntityType } from "../../sizersShared/sizerSelectOptions";

export default function EntitySection({ first }) {
  const { input: entityTypeInput } = useField("entity.entityType", {
    subscription: { value: true },
  });
  const entityType = entityTypeInput.value;

  return (
    <Section
      first={first}
      title="Borrowing entity"
      subtitle="The entity taking the loan. Applicants are its guarantors."
    >
      <Grid cols={3}>
        {/* SCENARIO AID, not a loan field: how many owners/members the entity
            has, so create-deal knows how much entity detail is still to be
            gathered. It is stripped from the Save Loan payload — on a real loan
            the members are `entities[].entityMembers[]`, actual records rather
            than a count. */}
        <Field2
          label="Number of entity members or owners"
          hint="Owners to reconcile at create-deal — guarantors are entered below."
        >
          <RffStepper name="numberOfEntityMembers" placeholder="0" min={0} />
        </Field2>
        <Field2 label="Entity type">
          {/* Values are the model's `entityType` options verbatim — including
              its "Irrecovable Trust" spelling. Matching the enum exactly is the
              contract; correcting the typo here would post a value the backend
              rejects. */}
          <RffSelect
            name="entity.entityType"
            options={selectOptionsEntityType}
            placeholder="Select entity type"
          />
        </Field2>
        <Field2 label="Entity name">
          <RffText
            name="entity.entityName"
            placeholder={
              entityType === "TBD"
                ? "Entity type not yet decided"
                : "e.g. Acme Holdings LLC"
            }
          />
        </Field2>
      </Grid>
    </Section>
  );
}
